import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { TransferRequestResponse } from '@/shared/api/types'
import { StationPicker } from './IntakeDialog'
import { TRANSFER_REASON_LABEL, updateTransfer } from './api'

const PRIORITIES = ['P1', 'P2', 'P3', 'P4']

/**
 * Correcting a draft.
 *
 * Someone picks the wrong station, or types the wrong reason, and without this
 * the only remedy is to withdraw the request and raise another — which leaves a
 * withdrawal in the permanent trail that says nothing true about the voter.
 *
 * Two rules are visible in the code:
 *
 * 1. **DRAFT only.** Once a request has been submitted it has been checked, and
 *    editing it underneath a reviewer is exactly what the append-only trail
 *    exists to prevent. The server refuses it; this dialog is not offered.
 * 2. **`If-Match` carries the version.** Two people editing the same draft from
 *    two screens must not both win silently. The loser gets a 412 with the
 *    server's own sentence, which says what to do about it.
 *
 * `rationale` and the voter's own words are write-only in the API — they are
 * accepted on create and update but are not on `TransferRequestResponse` — so
 * they cannot be pre-filled here. Left blank, they are not sent and the stored
 * text stands; typed into, they replace it.
 */
export function EditDraftDialog({
  request, onClose, onSaved,
}: {
  request: TransferRequestResponse
  onClose: () => void
  onSaved: () => void
}) {
  const [toStationId, setToStationId] = useState(request.toStationId ?? '')
  // The name if the server resolved one, the station's own code otherwise — never the
  // whole ltree path, which is what this used to show in a field labelled with a name.
  const [toStationLabel, setToStationLabel] = useState(
    request.toStationName ?? (request.toUnitPath ?? '').split('.').pop() ?? '')
  const [reasonCode, setReasonCode] = useState(request.reason)
  const [priority, setPriority] = useState(request.priority ?? 'P3')
  const [rationale, setRationale] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const rationaleTooShort = rationale.trim().length > 0 && rationale.trim().length < 20
  const ready = toStationId !== '' && !rationaleTooShort

  return (
    <Modal
      open
      onClose={onClose}
      title="Correct this draft"
      description="Nothing here has been submitted yet, so a correction is a correction — not a new request."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!ready || saving}
            onClick={async () => {
              setSaving(true); setError(null)
              try {
                await updateTransfer(request.id, {
                  toStationId,
                  reasonCode,
                  priority,
                  ...(rationale.trim() ? { rationale: rationale.trim() } : {}),
                }, request.version)
                onSaved()
              } catch (e) { setError(explain(e)); setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </>
      }
    >
      <ErrorStrip message={error} className="mb-3 border rounded-md" />

      <div className="space-y-3">
        <StationPicker
          value={toStationId}
          label={toStationLabel}
          onPick={(id, label) => { setToStationId(id); setToStationLabel(label) }}
        />

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-erp-xs text-muted-foreground">Reason</span>
            <select className="erp-input w-full mt-1" value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}>
              {Object.entries(TRANSFER_REASON_LABEL).map(([c, l]) => (
                <option key={c} value={c}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-erp-xs text-muted-foreground">Priority</span>
            <select className="erp-input w-full mt-1" value={priority}
                    onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">
            Replace your summary for the reviewer <span className="opacity-70">(optional)</span>
          </span>
          <textarea
            className="erp-input w-full mt-1 min-h-[72px]"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Leave blank to keep what is already stored."
          />
          {rationaleTooShort && (
            <p className="text-erp-xs text-amber-700 dark:text-amber-400 mt-1">
              At least 20 characters, or leave it empty.
            </p>
          )}
        </label>

        <Note tone="info">
          Saved against version {request.version}. If someone else has edited this draft since
          this screen loaded, the save is refused rather than overwriting their work.
        </Note>
      </div>
    </Modal>
  )
}
